import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, DollarSign, Target, ChevronRight, Check,
  Clock, Zap, TrendingUp, Star, Award,
} from 'lucide-react';

// ─── The $100 Night Club ────────────────────────────────────────────
// Not about rankings. Not about being the best.
// Just about hitting YOUR number tonight.
// What does it take to walk out with $100? $150? $200?
// Break it down so simply that it doesn't feel impossible.

interface EarningsTarget {
  amount: number;
  label: string;
  meansTo: string;
  tablesNeeded: number;
  upsellsNeeded: number;
  drinksNeeded: number;
  difficulty: string;
}

const TARGETS: EarningsTarget[] = [
  {
    amount: 80,
    label: 'The Safety Net',
    meansTo: 'Covers tomorrow\'s food and bus fare',
    tablesNeeded: 5,
    upsellsNeeded: 0,
    drinksNeeded: 0,
    difficulty: 'Just show up and do your job. You\'ll hit this.',
  },
  {
    amount: 120,
    label: 'The Comfortable Night',
    meansTo: 'Food for the next 3 days + a little breathing room',
    tablesNeeded: 5,
    upsellsNeeded: 2,
    drinksNeeded: 3,
    difficulty: 'Mention the Wagyu twice and push the Mango Lassi. That\'s all.',
  },
  {
    amount: 160,
    label: 'The Good Night',
    meansTo: 'Half your weekly groceries handled in one shift',
    tablesNeeded: 6,
    upsellsNeeded: 4,
    drinksNeeded: 5,
    difficulty: 'Pitch an upsell to every other table. You know the script. Use it.',
  },
  {
    amount: 200,
    label: 'The Great Night',
    meansTo: 'Rent contribution for the week. Real progress.',
    tablesNeeded: 7,
    upsellsNeeded: 6,
    drinksNeeded: 8,
    difficulty: 'Every table gets a recommendation. Every table gets a drink suggestion. Full effort.',
  },
  {
    amount: 250,
    label: 'The Legendary Night',
    meansTo: 'This shift changes your week. You feel it in your wallet.',
    tablesNeeded: 8,
    upsellsNeeded: 8,
    drinksNeeded: 10,
    difficulty: 'You\'re on fire tonight. Every table, every pitch, every opportunity. This is what it looks like when it all clicks.',
  },
];

// Simulated current progress
const CURRENT = {
  earned: 94.80,
  tablesServed: 4,
  tablesRemaining: 3,
  upsellsLanded: 3,
  drinksLanded: 4,
  hoursIn: 2.5,
  hoursLeft: 3,
};

const HundredDollarNight: React.FC = () => {
  const navigate = useNavigate();
  const [selectedTarget, setSelectedTarget] = useState(2); // $160 default

  const target = TARGETS[selectedTarget];
  const pct = Math.min((CURRENT.earned / target.amount) * 100, 100);
  const remaining = Math.max(target.amount - CURRENT.earned, 0);
  const perTableNeeded = CURRENT.tablesRemaining > 0 ? remaining / CURRENT.tablesRemaining : 0;
  const onPace = (CURRENT.earned / CURRENT.hoursIn) * (CURRENT.hoursIn + CURRENT.hoursLeft);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <button onClick={() => navigate('/restaurant/mockups')} className="flex items-center gap-1.5 text-gray-400 hover:text-gray-600 text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>
          <h1 className="text-xl font-bold text-gray-900 mb-1">Set Your Number</h1>
          <p className="text-sm text-gray-500">
            Pick a target. See exactly what it takes. Then go get it.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        {/* Target selector */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {TARGETS.map((t, i) => (
            <button
              key={t.amount}
              onClick={() => setSelectedTarget(i)}
              className={`flex-shrink-0 px-4 py-2.5 rounded-xl border text-center transition-all ${
                selectedTarget === i
                  ? 'bg-gray-900 border-gray-900 text-white shadow-lg'
                  : CURRENT.earned >= t.amount
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                    : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="text-lg font-bold">${t.amount}</div>
              <div className="text-[10px] opacity-70">{t.label}</div>
            </button>
          ))}
        </div>

        {/* Progress toward selected target */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-base font-semibold text-gray-900">${target.amount} — {target.label}</h2>
            {CURRENT.earned >= target.amount && (
              <span className="flex items-center gap-1 text-sm font-bold text-emerald-600">
                <Check className="w-4 h-4" /> Hit!
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mb-4">{target.meansTo}</p>

          {/* Big progress bar */}
          <div className="relative mb-2">
            <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ${
                  pct >= 100 ? 'bg-emerald-500' : pct >= 60 ? 'bg-blue-500' : 'bg-gray-400'
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
            {/* Dollar markers */}
            <div className="absolute inset-0 flex items-center px-3 pointer-events-none">
              <span className="text-xs font-bold text-white drop-shadow">
                ${CURRENT.earned.toFixed(0)}
              </span>
            </div>
          </div>
          <div className="flex justify-between text-xs text-gray-400 mb-4">
            <span>$0</span>
            <span>${target.amount}</span>
          </div>

          {CURRENT.earned < target.amount ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <span className="font-bold">${remaining.toFixed(2)} to go.</span>{' '}
                You have {CURRENT.tablesRemaining} tables left.
                That's about <span className="font-bold">${perTableNeeded.toFixed(0)} per table</span> — very doable.
              </p>
            </div>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <p className="text-sm text-emerald-800">
                You already hit ${target.amount}. <span className="font-bold">Nice.</span> Set the next target?
              </p>
            </div>
          )}
        </div>

        {/* The breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">What ${target.amount} looks like</h2>
          <p className="text-xs text-gray-500 mb-4">{target.difficulty}</p>

          <div className="space-y-4">
            {/* Tables */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-700">Tables to serve</span>
                </div>
                <span className="text-sm font-medium">
                  <span className="text-emerald-600">{CURRENT.tablesServed}</span>
                  <span className="text-gray-400"> / {target.tablesNeeded}</span>
                </span>
              </div>
              <div className="flex gap-1.5">
                {Array.from({ length: target.tablesNeeded }).map((_, i) => (
                  <div
                    key={i}
                    className={`h-2 flex-1 rounded-full ${
                      i < CURRENT.tablesServed ? 'bg-emerald-400' : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Upsells */}
            {target.upsellsNeeded > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm text-gray-700">Upsells to land</span>
                  </div>
                  <span className="text-sm font-medium">
                    <span className="text-emerald-600">{CURRENT.upsellsLanded}</span>
                    <span className="text-gray-400"> / {target.upsellsNeeded}</span>
                  </span>
                </div>
                <div className="flex gap-1.5">
                  {Array.from({ length: target.upsellsNeeded }).map((_, i) => (
                    <div
                      key={i}
                      className={`h-2 flex-1 rounded-full ${
                        i < CURRENT.upsellsLanded ? 'bg-emerald-400' : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Drinks */}
            {target.drinksNeeded > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-blue-500" />
                    <span className="text-sm text-gray-700">Drinks to push</span>
                  </div>
                  <span className="text-sm font-medium">
                    <span className="text-emerald-600">{CURRENT.drinksLanded}</span>
                    <span className="text-gray-400"> / {target.drinksNeeded}</span>
                  </span>
                </div>
                <div className="flex gap-1.5">
                  {Array.from({ length: target.drinksNeeded }).map((_, i) => (
                    <div
                      key={i}
                      className={`h-2 flex-1 rounded-full ${
                        i < CURRENT.drinksLanded ? 'bg-blue-400' : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pace check */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-900">Pace Check</h2>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-lg font-bold text-gray-900">${(CURRENT.earned / CURRENT.hoursIn).toFixed(0)}/hr</div>
              <div className="text-[10px] text-gray-500">current rate</div>
            </div>
            <div className={`rounded-lg p-3 ${onPace >= target.amount ? 'bg-emerald-50' : 'bg-amber-50'}`}>
              <div className={`text-lg font-bold ${onPace >= target.amount ? 'text-emerald-700' : 'text-amber-700'}`}>
                ${onPace.toFixed(0)}
              </div>
              <div className="text-[10px] text-gray-500">on pace for</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-lg font-bold text-gray-900">{CURRENT.hoursLeft}h</div>
              <div className="text-[10px] text-gray-500">remaining</div>
            </div>
          </div>
          {onPace >= target.amount ? (
            <p className="text-xs text-emerald-600 text-center mt-3">
              You're on pace to hit ${target.amount}. Keep this energy.
            </p>
          ) : (
            <p className="text-xs text-amber-600 text-center mt-3">
              Pick up the pace a little — push one more upsell per hour to get there.
            </p>
          )}
        </div>

        {/* Motivational close */}
        <div className="text-center py-4">
          <p className="text-sm text-gray-500">
            It's not about being the best server in the building.<br />
            It's about hitting <span className="font-medium text-gray-900">your</span> number tonight.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HundredDollarNight;
